import { useState, useEffect } from "react";
import { User, UserPlus, Check } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useSearchPatients } from "@/hooks/usePatients";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { useCanViewPhoneNumbers } from "@/hooks/useCanViewPhoneNumbers";
import { PhoneInputWithCode } from "@/components/ui/phone-input-with-code";

interface PatientData {
  id: string;
  name: string;
  phone: string;
  registration_number?: string;
}

interface PhoneNumberPatientSearchProps {
  value?: string;
  onPhoneChange: (phone: string) => void;
  onPatientSelect: (patient: PatientData | null) => void;
  selectedPatient: PatientData | null;
}

export const PhoneNumberPatientSearch = ({
  value = "",
  onPhoneChange,
  onPatientSelect,
  selectedPatient,
}: PhoneNumberPatientSearchProps) => {
  const { canViewPhoneNumbers } = useCanViewPhoneNumbers();
  const [phoneInput, setPhoneInput] = useState(value);
  const [showResults, setShowResults] = useState(false);
  
  // Extract just the number part for search (without country code)
  const searchNumber = phoneInput.replace(/^\+\d+\s*/, '').replace(/\D/g, '');
  const { data: patients = [], isLoading } = useSearchPatients(searchNumber);

  useEffect(() => {
    setPhoneInput(value);
  }, [value]);

  const handlePhoneChange = (newPhone: string) => {
    setPhoneInput(newPhone);
    onPhoneChange(newPhone);
    
    // Extract number part for showing results
    const numberPart = newPhone.replace(/^\+\d+\s*/, '').replace(/\D/g, '');
    setShowResults(numberPart.length >= 3);
    
    // If phone is cleared, clear selection
    if (!newPhone || numberPart.length === 0) {
      onPatientSelect(null);
    }
  };

  const handlePatientClick = (patient: PatientData) => {
    onPatientSelect(patient);
    setPhoneInput(patient.phone);
    onPhoneChange(patient.phone);
    setShowResults(false);
  };

  const handleClearSelection = () => {
    onPatientSelect(null);
    const numberPart = phoneInput.replace(/^\+\d+\s*/, '').replace(/\D/g, '');
    setShowResults(numberPart.length >= 3);
  };

  // Prioritize phone matches
  const sortedPatients = [...patients].sort((a, b) => {
    const phoneA = (a.phone || "").replace(/\D/g, '');
    const phoneB = (b.phone || "").replace(/\D/g, '');
    const search = searchNumber;

    // Exact match
    if (phoneA.endsWith(search)) return -1;
    if (phoneB.endsWith(search)) return 1;

    // Contains
    if (phoneA.includes(search) && !phoneB.includes(search)) return -1;
    if (phoneB.includes(search) && !phoneA.includes(search)) return 1;

    return 0;
  });

  const hasResults = sortedPatients.length > 0;
  const showNoResults = showResults && !hasResults && !isLoading && searchNumber.length >= 3;

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label htmlFor="phone">Patient Phone Number *</Label>
        <div className="relative">
          <PhoneInputWithCode
            value={phoneInput}
            onChange={handlePhoneChange}
            placeholder="Enter phone number to search..."
            className={cn(selectedPatient && "[&_input]:border-primary")}
          />
          {selectedPatient && (
            <button
              type="button"
              onClick={handleClearSelection}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground z-10"
            >
              ×
            </button>
          )}
        </div>
        {searchNumber.length > 0 && searchNumber.length < 3 && (
          <p className="text-xs text-muted-foreground">
            Type at least 3 digits to search...
          </p>
        )}
      </div>

      {/* Selected Patient Card */}
      {selectedPatient && (
        <Card className="p-3 bg-primary/5 border-primary">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Check className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-foreground">{selectedPatient.name}</p>
              {canViewPhoneNumbers && <p className="text-sm text-muted-foreground">{selectedPatient.phone}</p>}
              {selectedPatient.registration_number && (
                <p className="text-xs text-muted-foreground">
                  Reg: {selectedPatient.registration_number}
                </p>
              )}
            </div>
            <User className="w-5 h-5 text-primary" />
          </div>
        </Card>
      )}

      {/* Search Results */}
      {showResults && !selectedPatient && (
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {isLoading && (
            <Card className="p-3">
              <p className="text-sm text-muted-foreground">Searching...</p>
            </Card>
          )}

          {hasResults && !isLoading && (
            <>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Found {sortedPatients.length} patient{sortedPatients.length !== 1 ? 's' : ''}
              </p>
              {sortedPatients.map((patient) => (
                <Card
                  key={patient.id}
                  className="p-3 cursor-pointer hover:bg-accent hover:border-primary transition-colors"
                  onClick={() => handlePatientClick(patient)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                      <User className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{patient.name}</p>
                      {canViewPhoneNumbers && <p className="text-sm text-muted-foreground">{patient.phone}</p>}
                      {patient.registration_number && (
                        <p className="text-xs text-muted-foreground">
                          Reg: {patient.registration_number}
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </>
          )}

          {showNoResults && (
            <Card className="p-4 bg-muted/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <UserPlus className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-foreground">No existing patient found</p>
                  <p className="text-sm text-muted-foreground">
                    Creating new appointment for this number
                  </p>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};
